// Dependency-free HTML sanitizer for user-authored rich-text notes.
//
// Notes are edited via a contentEditable editor (see RichTextEditor) and can be
// rendered back to OTHER members of a shared space list — i.e. stored content
// rendered as HTML is a stored-XSS surface. Every render path MUST route the
// stored string through `sanitizeHtml` before injecting it into the DOM, and we
// also sanitize on save as defense in depth.
//
// Strategy: parse into a detached document, then walk the tree.
//   - tags that carry executable/opaque content are removed entirely
//   - tags outside the allow-list are UNWRAPPED (their text is kept)
//   - allowed tags keep only a tiny attribute allow-list (safe <a href>)

/** Formatting tags produced by the editor's execCommand toolbar. */
const ALLOWED_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "DEL",
  "BR", "P", "DIV", "SPAN",
  "UL", "OL", "LI",
  "BLOCKQUOTE", "CODE", "PRE",
  "H1", "H2", "H3",
  "A",
]);

/** Tags whose entire subtree is dropped (never just unwrapped). */
const DROP_WITH_CONTENT = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META",
  "NOSCRIPT", "TEMPLATE", "TITLE", "HEAD", "FRAME", "FRAMESET",
]);

/** Returns true if the href is a safe (non-scripting) URL. */
function isSafeHref(raw: string): boolean {
  const v = raw.trim();
  if (v === "") return false;
  // Relative / anchor / query links are safe (no protocol).
  if (/^(#|\/|\.\/|\.\.\/|\?)/.test(v)) return true;
  // eslint-disable-next-line no-control-regex
  const stripped = v.replace(/[ -\s]/g, "").toLowerCase();
  return (
    stripped.startsWith("http://") ||
    stripped.startsWith("https://") ||
    stripped.startsWith("mailto:") ||
    stripped.startsWith("tel:")
  );
}

/** Recursively cleans a node's element children in place. */
function cleanChildren(node: Node): void {
  // Snapshot children — we mutate the live list while iterating.
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === 3 /* text */ || child.nodeType === 8 /* comment */) {
      if (child.nodeType === 8) child.parentNode?.removeChild(child);
      continue;
    }
    if (child.nodeType !== 1 /* element */) {
      child.parentNode?.removeChild(child);
      continue;
    }
    const el = child as Element;
    const tag = el.tagName.toUpperCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      el.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: clean its children first, then splice them in where it sat.
      cleanChildren(el);
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
      continue;
    }

    // Allowed tag — strip every attribute except a vetted <a href>.
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (tag === "A" && name === "href" && isSafeHref(attr.value)) continue;
      el.removeAttribute(attr.name);
    }
    if (tag === "A" && el.hasAttribute("href")) {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer nofollow");
    }

    cleanChildren(el);
  }
}

/** Sanitizes untrusted HTML down to a safe formatting subset. */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return "";
  // Pre-strip executable blocks so nothing can run even in environments whose
  // HTML parser is not fully inert. The DOM walk below is the real allow-list;
  // this is defense in depth.
  const pre = dirty
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<style\b[\s\S]*?<\/style\s*>/gi, "");
  // Parse into a DETACHED document (no browsing context) — assigning innerHTML
  // here never executes scripts, unlike a live/connected element.
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = pre;
  cleanChildren(doc.body);
  return doc.body.innerHTML;
}

/** True when the string appears to contain HTML markup (vs legacy plain text). */
export function looksLikeHtml(s: string): boolean {
  return /<[a-z!/][\s\S]*>/i.test(s);
}

/**
 * Decodes HTML entities (&nbsp; &amp; &#39; …) to their literal characters.
 * Writers that bypass the editor (MCP tools, the iOS share extension, …) store
 * `notes` as a bare string with no HTML awareness; text they copy from an HTML
 * source often already contains literal entity text (e.g. a copied "phone
 * &nbsp;name" snippet). Without this step `plainTextToHtml` below would escape
 * the leading "&" a second time, turning it into visible `&amp;nbsp;` — i.e.
 * the literal `&nbsp;` the user sees on screen instead of a space.
 */
function decodeHtmlEntities(s: string): string {
  if (!s.includes("&")) return s;
  const doc = document.implementation.createHTMLDocument("");
  const ta = doc.createElement("textarea");
  ta.innerHTML = s;
  return ta.value;
}

/** Escapes plain text for safe HTML rendering, mapping newlines to <br>. */
export function plainTextToHtml(s: string): string {
  const esc = decodeHtmlEntities(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return esc.replace(/\r?\n/g, "<br>");
}

/**
 * Normalizes a stored notes value to safe HTML for display/editing.
 * Legacy plain-text notes are escaped and given <br> line breaks; anything that
 * already looks like markup is run through the sanitizer.
 */
export function notesToSafeHtml(value: string | null | undefined): string {
  if (!value) return "";
  return looksLikeHtml(value) ? sanitizeHtml(value) : plainTextToHtml(value);
}
